// Single source of truth for the reading page.
// The section registry drives navigation, anchors, and active-section tracking.
// Article prose is preserved verbatim from the approved Founder's Note.
// Section headings exist only to give the Contents navigation stable anchors.

export const META = {
  title: "The Accumulation of Judgment",
  author: "Aria Mohajer",
  byline: "A Founder’s Note by Aria Mohajer",
  kicker: "Mithara Institute",
  description:
    "A Founder’s Note by Aria Mohajer on preserving useful judgment beyond the person who developed it — the accumulation Mithara is building toward.",
};

// Content sections in reading order. `id` drives the anchor, the nav link,
// and the active-section tracking. Order here is the order everywhere.
export const SECTIONS = [
  { id: "origin", title: "The Accumulation of Judgment", nav: "Opening" },
  { id: "question", title: "The question behind Mithara", nav: "The question behind Mithara" },
  { id: "establish", title: "What we have to establish", nav: "What we have to establish" },
  { id: "accumulation", title: "Accumulation", nav: "Accumulation" },
  { id: "infrastructure", title: "Judgment as infrastructure", nav: "Judgment as infrastructure" },
  { id: "leave-behind", title: "What we leave behind", nav: "What we leave behind" },
];

// The Sources section is navigable but rendered separately (collapsible + printable).
export const SOURCES_SECTION = { id: "sources", kicker: "Reference", title: "Sources", nav: "Sources" };

// A citation anchor. Renders a superscript link to a source and carries the
// return target id so a reader can jump back from the source list.
function Cite({ n }) {
  return (
    <sup className="cite">
      <a id={`cite-${n}`} href={`#ref-${n}`} data-testid={`citation-link-${n}`}>
        [{n}]
      </a>
    </sup>
  );
}

// Body content keyed by section id. Paragraphs are verbatim.
export const CONTENT = {
  origin: (
    <>
      <p className="epigraph">
        A civilization should not spend generations creating judgment only to
        lose it one person at a time.
      </p>
      <p>
        I was born in Germany to Iranian parents. My father came to Germany at
        twelve, and my mother arrived in her late teens without speaking German.
        She learned the language, went on to study medicine, and became a
        cardiologist. When I think about her experience, I also think about
        everything she had to learn before her medical career could even begin.
      </p>
      <p>
        It was through our conversations about her work that I began to think
        more specifically about what decades of practice change in a person. She
        told me about a patient who came in for a routine heart examination.
        Although the initial results were reassuring, she decided to look beyond
        the usual scope of that examination and ordered a different test. It
        revealed a life-threatening condition; as she explained it to me, he
        needed surgery within the hour to survive.
      </p>
      <p>
        I kept returning to her decision to look further, before she knew what
        the additional test would reveal. What role had experience played in
        that decision? As I thought about it, I became interested in what it
        would take for a machine to learn from evidence of such judgments.
      </p>
    </>
  ),
  question: (
    <>
      <p>
        The approaches I have examined have not satisfied me on that point.
        Recognizing a pattern or reproducing an expert’s choice can be useful,
        but I want to know what the expert’s contribution actually adds to the
        system’s ability to decide and whether that benefit survives when the
        task changes. Where a machine has less information than the expert did,
        the best choice for one is not automatically the best choice for the
        other. That matters because preserving judgment has to mean preserving
        something useful under the conditions in which it will be used.
      </p>
      <p>
        That is why I am building Mithara around Judgment Models: to make useful
        judgment something that can be learned, tested, and built upon as
        people, circumstances, and problems change.
      </p>
      <p>
        We already carry an enormous amount forward through teaching, research,
        and the people we train, so every generation benefits from the work of
        others. Even so, I believe we should expect more from our ability to
        preserve what a lifetime teaches someone. An illustrative calculation
        based on AAMC’s 2024 retirement outlook puts the experience represented
        by projected U.S. physician retirements at roughly one million
        physician-years annually, averaged over a decade.
        <Cite n={1} /> The calculation cannot tell us how much of that
        experience remains useful, but it gives some sense of the careers behind
        the question.
      </p>
      <p>
        Medicine makes this personal for me. A study published in 2023 estimated
        that diagnostic errors cause nearly 800,000 deaths or permanent
        disabilities each year in the United States.
        <Cite n={2} /> I think about the family who has to live with the question
        of whether something could have been recognized sooner. We have not
        established what Judgment Models could change here, but I want to find
        out whether experience that was available somewhere else could have made
        a difference when it mattered. The same question extends to engineering
        and financial systems, where consequential decisions also depend on
        experience.
      </p>
    </>
  ),
  establish: (
    <>
      <p>
        I am asking people to commit serious work to this, so I owe them a clear
        account of where we stand. Our first controlled Judgment Model result
        has not been produced. The methodology sets out how we intend to test
        whether evidence from expert decisions adds value beyond strong existing
        approaches, what accounts for that value, and whether what is learned
        reduces the experience or feedback needed on new tasks. Each claim has
        to be established in its own right. And yes, I want us to move fast. That
        means reaching results we can examine and act on, with enough precision
        to know when we need to change our approach.
      </p>
      <p>
        That same standard has to apply to the experience we hope to preserve.
        Someone can spend years becoming confident in something they should
        reconsider, and an expert’s reputation cannot settle whether their
        judgment holds up in a new situation. If we lose the conditions that made
        a decision sensible, we risk making a mistake easier to repeat. For me,
        respecting someone’s experience includes taking it seriously enough to
        question it, even when I admire the person it came from.
      </p>
    </>
  ),
  accumulation: (
    <>
      <p>
        What draws me further is the possibility that a contribution could become
        more useful after it was made. A case encountered years later might
        reveal where an earlier judgment applies or why it fails, while an
        observation that remained unresolved could become useful once later work
        gives us a way to test it. Someone could contribute something whose full
        value they never had the chance to discover themselves. If that process
        works, the next generation could begin at a higher level of judgment and
        use more of its lifetime to go further, leaving a better starting point
        in turn.
      </p>
      <p>
        I also care about what this would do to us as people. Getting a better
        answer from AI is useful, but I want to know whether working with these
        systems can help someone become better at assessing a difficult
        situation themselves, including when the assistance is removed. We would
        have to measure that separately. The future I want Mithara to contribute
        to is one in which human capability grows alongside the systems we build.
      </p>
    </>
  ),
  infrastructure: (
    <>
      <p>
        If the research succeeds, the world I imagine is one in which
        institutions can draw on useful judgment developed far beyond their own
        walls. A physician in a smaller hospital could benefit from experience
        with cases their team rarely sees. An engineering firm could bring
        lessons from years of failure analysis into a design review; a bank could
        recognize a costly risk earlier and reserve more of its senior people’s
        attention for the decisions that need it most. Each application would
        need its own evidence. I see these capabilities becoming part of the
        systems people already work with, available when a consequential decision
        is being made.
      </p>
      <p>
        The economic possibility is that the value of an expert’s contribution
        could extend well beyond the hours they can personally work. A capability
        that reliably improves decisions could serve institutions for years,
        spreading the cost of developing and validating it across more
        deployments and supporting a profitable business. I care about building
        that business so that its success is shared: institutions make better
        decisions, experts share in the value of their contributions, and Mithara
        earns the means to keep improving the system and extending its reach. I
        want our growth to mean that more people can benefit from a lifetime of
        learning they would otherwise never have had access to.
      </p>
    </>
  ),
  "leave-behind": (
    <>
      <p>
        Ferdowsi comes to mind here, and I’ll admit that being Persian probably
        has something to do with that. The Shahnameh was completed more than a
        thousand years ago and remains part of our language and cultural memory.
        <Cite n={3} /> That continuity makes the question of what we leave behind
        feel immediate to me, because we are already living with what others
        managed to carry forward. It also gives me a reason to take a long
        horizon seriously while staying very concrete about the work in front of
        us.
      </p>
      <p>
        When I think about Mithara fifty or a hundred years from now, I imagine a
        machine drawing on something useful learned from my mother’s decisions,
        helping a physician care for a patient she would never have met. That
        physician would contribute experience of their own, including things she
        could never have known, and improve what the next person inherits. I
        believe a lifetime of learning deserves the chance to keep helping people
        in that way. That is the future I intend to build with Mithara.
      </p>
      <p className="signature">
        <span className="signature-name">Aria Mohajer</span>
        <span className="signature-role">Mithara Institute</span>
      </p>
    </>
  ),
};

const SourceLink = ({ href, children }) => (
  <a className="source-ext" href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

export const SOURCES = [
  {
    id: "ref-1",
    cite: "cite-1",
    text: (
      <>
        <strong>Source and calculation.</strong> GlobalData Plc. (2024),{" "}
        <em>
          The Complexities of Physician Supply and Demand: Projections From 2021
          to 2036
        </em>
        , AAMC, pp. viii and 25. The report describes retirement within a decade
        as likely for more than one-third of active physicians and uses a 2021
        baseline of approximately 854,000 physicians. Using one-third as an
        illustrative benchmark and assuming 30–40 years of experience per
        retiring physician gives 854,000 × ⅓ ÷ 10 × 30–40 ≈ 0.85–1.14 million
        physician-years annually. This is a Mithara-derived annualized estimate;
        the experience range is an assumption, not a measured national average.
        It does not measure knowledge lost at retirement.{" "}
        <SourceLink href="https://www.aans.org/wp-content/uploads/2024/05/aamc-2023-workforce-projections-report.pdf">
          Read the report
        </SourceLink>
        .
      </>
    ),
  },
  {
    id: "ref-2",
    cite: "cite-2",
    text: (
      <>
        Newman-Toker, D. E., et al.,{" "}
        <em>Burden of serious harms from diagnostic error in the USA</em>, BMJ
        Quality &amp; Safety (2024); first published online July 17, 2023. The
        study estimated approximately 795,000 deaths or permanent disabilities
        annually across U.S. care settings, using disease incidence and
        disease-specific error and harm estimates, with extrapolation across
        dangerous diseases. This is a modeled national estimate, not a direct
        annual count or an estimate of harm preventable by Judgment Models.{" "}
        <SourceLink href="https://doi.org/10.1136/bmjqs-2021-014130">
          Original paper
        </SourceLink>
        .{" "}
        <SourceLink href="https://www.hopkinsmedicine.org/news/newsroom/news-releases/2023/07/report-highlights-public-health-impact-of-serious-harms-from-diagnostic-error-in-us">
          Johns Hopkins Medicine summary
        </SourceLink>
        .
      </>
    ),
  },
  {
    id: "ref-3",
    cite: "cite-3",
    text: (
      <>
        Library of Congress, <em>The Epic of Shahnameh</em>, in{" "}
        <em>A Thousand Years of the Persian Book</em>. The exhibition dates the
        work’s first appearance to 1010 and describes its continuing influence on
        Persian language and culture.{" "}
        <SourceLink href="https://www.loc.gov/exhibits/thousand-years-of-the-persian-book/epic-of-shahnameh.html">
          Read the source
        </SourceLink>
        .
      </>
    ),
  },
];
